import {formatCurrency, formatDate, formatNumber} from '@angular/common';
import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardCompanyProductUse, AdminDashboardPoint} from '@features/admin-panel/models/admin-dashboard.models';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbPct, adbUsdCompact} from '@features/admin-panel/helpers/admin-dashboard-format';
import {
  adbAlpha,
  adbCategoryAxis,
  adbChartTheme,
  adbGrid,
  adbLegend,
  adbRedrawOnThemeChange,
  adbTooltip,
  adbValueAxis
} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const c360ProductUseCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The spend axis: "$12.5k" from $1,000 up (adbUsdCompact), else up to two decimals ("$1.5", "$0"). */
export function c360SpendAxisText(value: number): string {
  return Math.abs(value) >= 1000 ? adbUsdCompact(value) : formatCurrency(value, 'en-US', '$', 'USD', '1.0-2');
}

/** A quality month's axis label: "Nov" (UTC); the current (last) month reads "Oct to date" (ITP-E-167-07). */
export function c360QualityMonthLabel(month: string, current: boolean): string {
  return `${formatDate(month, 'MMM', 'en-US', 'UTC')}${current ? ' to date' : ''}`;
}

const usd = (value: number): string => formatCurrency(value ?? 0, 'en-US', '$', 'USD', '1.2-2');
const total = (points: AdminDashboardPoint[]): number => (points ?? []).reduce((sum, point) => sum + (point.value ?? 0), 0);

/**
 * Company 360 "Product use" (shown only with `APUsageReport`, GEN-E-182-27; OCA-E-159 M/company/[id]/page.tsx:571-596): the XatBot
 * questions (violet bars) and spend (dark line, $ axis) chart, the 30-day quality stats, the entity count, the
 * package pills, and a link to the company's E.A.T. usage report.
 * ITP-E-167-07: with `MonthlyQuality` (ITP-E-167-06) a second chart, "Unresolved rate and negative feedback" by month
 * (negative ratings as purple bars, the unresolved rate as a red line on a % axis), sits beside the first (6 / 6) and the
 * quality / entities / packages card spans the row; an older response keeps the R4 layout (8 / 4).
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-c360-product-use',
  templateUrl: './admin-dashboard-c360-product-use.component.html',
  styleUrls: ['./admin-dashboard-c360-product-use.component.scss']
})
export class AdminDashboardC360ProductUseComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => {
    this.render();
    this.renderQuality();
  });
  @Input() companyId: string | null = null;
  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;

  productUse: AdminDashboardCompanyProductUse | null = null;

  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;
  private qualityElement: HTMLDivElement;
  private qualityChart: echarts.ECharts;
  private qualityResizeObserver: ResizeObserver;

  constructor(private dataService: AdminDashboardDataService, public router: Router) {}

  /** The quality chart's container exists only while a month has questions: the chart is made and disposed with it. */
  @ViewChild('qualityChart') set qualityChartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.qualityElement) {
      return;
    }
    this.disposeQualityChart();
    if (container) {
      this.qualityElement = container.nativeElement;
      this.qualityChart = c360ProductUseCharts.init(this.qualityElement);
      this.qualityResizeObserver = new ResizeObserver(() => this.qualityChart?.resize());
      this.qualityResizeObserver.observe(this.qualityElement);
      this.renderQuality();
    }
  }

  /** "8 questions and $3.50 spend over the last 90 days." */
  get summary(): string {
    const questions = total(this.productUse?.Questions);
    const spend = total(this.productUse?.Spend);
    return `${countOf(questions, 'question')} and ${usd(spend)} spend over the last 90 days.`;
  }

  /** True when the response carries the 12 quality months (ITP-E-167-06); an older API keeps the R4 layout. */
  get hasMonthlyQuality(): boolean {
    return Array.isArray(this.productUse?.MonthlyQuality);
  }

  /** True when any of the 12 months has a question; otherwise the quality card shows its empty state. */
  get qualityHasQuestions(): boolean {
    return (this.productUse?.MonthlyQuality ?? []).some(month => (month.questions ?? 0) > 0);
  }

  /** "Collecting history — XatBot quality is recorded from 14 Apr 2026." while the rollup is younger than the 12 months; else ''. */
  get qualityHistoryText(): string {
    const from = this.productUse?.QualityHistoryFrom;
    return from ? `Collecting history — XatBot quality is recorded from ${formatDate(from, 'd MMM y', 'en-US', 'UTC')}.` : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId']) {
      this.load();
    }
  }

  ngAfterViewInit(): void {
    this.chart = c360ProductUseCharts.init(this.chartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.render();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
    this.disposeQualityChart();
  }

  /** "<b>2 Oct 2026</b><br/>Questions: <b>5</b><br/>Spend: <b>$2.00</b>". */
  tooltipText(index: number): string {
    const question = this.productUse?.Questions?.[index];
    if (!question) {
      return '';
    }
    const spend = this.productUse?.Spend?.[index]?.value ?? 0;
    return `<b>${formatDate(question.date, 'd MMM y', 'en-US', 'UTC')}</b><br/>`
      + `Questions: <b>${formatNumber(question.value ?? 0, 'en-US', '1.0-0')}</b><br/>Spend: <b>${usd(spend)}</b>`;
  }

  /**
   * "<b>Oct 2026 to date</b><br/>Unresolved: <b>25.0%</b><br/>Negative ratings: <b>3</b><br/>Questions: <b>40</b>";
   * a month under 10 questions has no rate and says "–".
   */
  qualityTooltipText(index: number): string {
    const months = this.productUse?.MonthlyQuality ?? [];
    const month = months[index];
    if (!month) {
      return '';
    }
    const current = index === months.length - 1;
    const rate = month.unresolvedRate === null || month.unresolvedRate === undefined ? '–' : adbPct(month.unresolvedRate, 1);
    return `<b>${formatDate(month.month, 'MMM y', 'en-US', 'UTC')}${current ? ' to date' : ''}</b><br/>`
      + `Unresolved: <b>${rate}</b><br/>`
      + `Negative ratings: <b>${formatNumber(month.negative ?? 0, 'en-US', '1.0-0')}</b><br/>`
      + `Questions: <b>${formatNumber(month.questions ?? 0, 'en-US', '1.0-0')}</b>`;
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.companyId) {
      return;
    }
    this.loadSubscription = this.dataService.getCompanyProductUse(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyProductUse))
    ).subscribe(productUse => {
      this.productUse = productUse;
      this.render();
      this.renderQuality();
    });
  }

  private render(): void {
    if (!this.chart) {
      return;
    }
    const t = adbChartTheme(this.chartContainer.nativeElement);
    const questions = this.productUse?.Questions ?? [];
    const spend = this.productUse?.Spend ?? [];
    const option: echarts.EChartsOption = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid(),
      legend: {...adbLegend(t), data: ['Questions', 'Spend']},
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => this.tooltipText((Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0)
      }),
      xAxis: adbCategoryAxis(t, questions.map(point => formatDate(point.date, 'd MMM', 'en-US', 'UTC'))),
      yAxis: [
        adbValueAxis(t, value => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
        adbValueAxis(t, c360SpendAxisText, {splitLine: {show: false}})
      ],
      series: [
        {
          type: 'bar', name: 'Questions', data: questions.map(point => point.value), barMaxWidth: 16,
          itemStyle: {color: adbAlpha(t.purple, 0.55), borderRadius: [2, 2, 0, 0]}
        },
        {
          type: 'line', name: 'Spend', yAxisIndex: 1, data: spend.map(point => point.value), showSymbol: false,
          connectNulls: true, lineStyle: {width: 2, color: t.ink}, itemStyle: {color: t.ink}
        }
      ]
    };
    this.chart.setOption(option, true);
  }

  /**
   * The OCA-E-159 quality combo (M/company/[id]/page.tsx:163-170): negative ratings as purple bars at 50% on the count
   * axis, the unresolved rate as a red line on the right % axis. A month under 10 questions has a null rate: no point,
   * and the line is not bridged across it (connectNulls false, symbols on so a lone rated month still shows).
   */
  private renderQuality(): void {
    if (!this.qualityChart) {
      return;
    }
    const months = this.productUse?.MonthlyQuality ?? [];
    const t = adbChartTheme(this.qualityElement);
    const option: echarts.EChartsOption = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid(),
      legend: {...adbLegend(t), data: ['Negative ratings', 'Unresolved']},
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (params: any) => this.qualityTooltipText((Array.isArray(params) ? params[0] : params)?.dataIndex ?? 0)
      }),
      xAxis: adbCategoryAxis(t, months.map((month, index) => c360QualityMonthLabel(month.month, index === months.length - 1))),
      yAxis: [
        adbValueAxis(t, value => formatNumber(value, 'en-US', '1.0-0'), {minInterval: 1}),
        adbValueAxis(t, value => adbPct(value), {min: 0, splitLine: {show: false}})
      ],
      series: [
        {
          type: 'bar', name: 'Negative ratings', data: months.map(month => month.negative), barMaxWidth: 16,
          itemStyle: {color: adbAlpha(t.purple, 0.5), borderRadius: [2, 2, 0, 0]}
        },
        {
          type: 'line', name: 'Unresolved', yAxisIndex: 1, data: months.map(month => month.unresolvedRate),
          showSymbol: true, connectNulls: false, lineStyle: {width: 2, color: t.red}, itemStyle: {color: t.red}
        }
      ]
    };
    this.qualityChart.setOption(option, true);
  }

  private disposeQualityChart(): void {
    this.qualityResizeObserver?.disconnect();
    this.qualityResizeObserver = null;
    this.qualityChart?.dispose();
    this.qualityChart = null;
    this.qualityElement = null;
  }
}
